import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Lock, LogOut, Download, Search, RefreshCw } from "lucide-react";
import { adminLogin, adminGetContacts, adminGetSubscribers } from "../lib/api";

const TOKEN_KEY = "hgdc_admin_token";
const field = "w-full border border-white/15 bg-ink px-4 py-4 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-gold";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const toCsv = (rows, cols) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const header = cols.map((c) => esc(c.label)).join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c.key])).join(",")).join("\n");
  return header + "\n" + body;
};

const download = (filename, text) => {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const LoginView = ({ onSuccess }) => {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const { token } = await adminLogin(password);
      sessionStorage.setItem(TOKEN_KEY, token);
      onSuccess(token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Incorrect password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6" data-testid="admin-login">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-ink-2 p-8 md:p-10">
        <div className="mb-6 flex h-12 w-12 items-center justify-center border border-gold/40 text-gold">
          <Lock className="h-5 w-5" />
        </div>
        <h1 className="font-display text-2xl font-light uppercase tracking-[0.1em] text-white">Admin Access</h1>
        <p className="mt-2 text-sm text-white/50">Enter the shared password to view submissions.</p>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          autoFocus
          data-testid="admin-password"
          className={`${field} mt-6`}
        />
        <button type="submit" disabled={loading} data-testid="admin-login-btn" className="mt-4 inline-flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne disabled:opacity-60">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </div>
  );
};

const TABS = [
  { key: "contacts", label: "Contact Messages" },
  { key: "subscribers", label: "Email Signups" },
];

const COLS = {
  contacts: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "phone", label: "Phone" },
    { key: "subject", label: "Subject" },
    { key: "message", label: "Message" },
    { key: "created_at", label: "Date" },
  ],
  subscribers: [
    { key: "name", label: "Name" },
    { key: "email", label: "Email" },
    { key: "interest", label: "Interest" },
    { key: "created_at", label: "Date" },
  ],
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  // Keep the admin dashboard out of search/AI indexes.
  useEffect(() => {
    const m = document.createElement("meta");
    m.name = "robots";
    m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => m.remove();
  }, []);

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([adminGetContacts(t), adminGetSubscribers(t)]);
      setContacts(c);
      setSubscribers(s);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        logout();
      } else {
        toast.error("Could not load data. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  if (!token) return <LoginView onSuccess={setToken} />;

  const rows = tab === "contacts" ? contacts : subscribers;
  const cols = COLS[tab];
  const q = query.trim().toLowerCase();
  const filtered = q
    ? rows.filter((r) => cols.some((c) => String(r[c.key] ?? "").toLowerCase().includes(q)))
    : rows;

  const exportCsv = () => {
    if (rows.length === 0) { toast.error("Nothing to export."); return; }
    const name = tab === "contacts" ? "contact-messages" : "email-signups";
    download(`higherground-${name}-${new Date().toISOString().slice(0, 10)}.csv`, toCsv(rows, cols));
    toast.success(`Exported ${rows.length} ${tab === "contacts" ? "messages" : "signups"}.`);
  };

  return (
    <div className="min-h-screen bg-ink px-6 pb-20 pt-28 lg:px-12" data-testid="admin-dashboard">
      <div className="mx-auto max-w-[1400px]">
        {/* Header */}
        <div className="flex flex-col gap-4 border-b border-white/10 pb-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Higher Ground DC</p>
            <h1 className="mt-2 font-display text-3xl font-light uppercase tracking-[0.08em] text-white">Submissions</h1>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => load(token)} data-testid="admin-refresh" className="inline-flex items-center gap-2 border border-white/20 px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-gold hover:text-gold">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-white/20 px-4 py-3 text-[11px] uppercase tracking-[0.2em] text-white/70 transition-colors hover:border-gold hover:text-gold">
              <LogOut className="h-4 w-4" /> Log Out
            </button>
          </div>
        </div>

        {/* Tabs + controls */}
        <div className="mt-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div className="flex gap-2">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] transition-colors ${tab === t.key ? "bg-gold text-ink" : "border border-white/15 text-white/60 hover:text-white"}`}
              >
                {t.label} ({t.key === "contacts" ? contacts.length : subscribers.length})
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search…"
                data-testid="admin-search"
                className="border border-white/15 bg-ink py-3 pl-10 pr-4 text-sm text-white placeholder-white/35 outline-none focus:border-gold"
              />
            </div>
            <button onClick={exportCsv} data-testid="admin-export" className="inline-flex items-center gap-2 bg-gold px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-ink transition-colors hover:bg-champagne">
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="mt-8 overflow-x-auto border border-white/10">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-white/50"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="py-24 text-center text-white/45" data-testid="admin-empty">No records found.</div>
          ) : (
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-white/10 bg-ink-2">
                <tr>
                  {cols.map((c) => (
                    <th key={c.key} className="px-5 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-gold">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="admin-table-body">
                {filtered.map((r, i) => (
                  <tr key={i} className="border-b border-white/5 align-top transition-colors hover:bg-white/[0.03]">
                    {cols.map((c) => (
                      <td key={c.key} className={`px-5 py-4 ${c.key === "created_at" ? "whitespace-nowrap text-white/50" : "text-white/80"} ${c.key === "message" ? "max-w-md" : ""}`}>
                        {c.key === "email" ? (
                          <a href={`mailto:${r[c.key]}`} className="text-white transition-colors hover:text-gold">{r[c.key]}</a>
                        ) : c.key === "created_at" ? (
                          fmtDate(r[c.key])
                        ) : (
                          r[c.key] || <span className="text-white/25">—</span>
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <p className="mt-4 text-xs text-white/40">Showing {filtered.length} of {rows.length}. Export downloads all {rows.length} records in this tab.</p>
      </div>
    </div>
  );
}
